import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ThumbsUp, ThumbsDown, User, Calendar, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

interface FeedbackItem {
  id: string;
  feedback_type: 'like' | 'dislike';
  reason?: string;
  created_at: string;
  user_email?: string;
  content_title?: string;
  content_prompt?: string;
}

export const FeedbackManagement = () => {
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ likes: 0, dislikes: 0, total: 0 });

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      // 1) Fetch raw feedback rows
      const { data: fb, error: fbError } = await supabase
        .from('feedback')
        .select('id, user_id, content_item_id, feedback_type, reason, created_at')
        .order('created_at', { ascending: false });

      if (fbError) throw fbError;

      // 2) Collect unique ids and fetch related data in bulk
      const userIds = Array.from(new Set((fb || []).map((f: any) => f.user_id).filter(Boolean)));
      const contentIds = Array.from(new Set((fb || []).map((f: any) => f.content_item_id).filter(Boolean)));

      const [profilesRes, contentRes] = await Promise.all([
        userIds.length
          ? supabase.from('profiles').select('user_id, email').in('user_id', userIds)
          : Promise.resolve({ data: [], error: null } as any),
        contentIds.length
          ? supabase.from('content_items').select('id, title, prompt').in('id', contentIds)
          : Promise.resolve({ data: [], error: null } as any)
      ]);

      if ((profilesRes as any).error) throw (profilesRes as any).error;
      if ((contentRes as any).error) throw (contentRes as any).error;

      const emailByUserId = new Map<string, string>(
        ((profilesRes as any).data || []).map((p: any) => [p.user_id, p.email])
      );
      const contentById = new Map<string, { title?: string; prompt?: string }>(
        ((contentRes as any).data || []).map((c: any) => [c.id, { title: c.title, prompt: c.prompt }])
      );

      // 3) Map into UI-friendly structure
      const formattedFeedback: FeedbackItem[] = (fb || []).map((item: any) => ({
        id: item.id,
        feedback_type: item.feedback_type,
        reason: item.reason,
        created_at: item.created_at,
        user_email: emailByUserId.get(item.user_id),
        content_title: contentById.get(item.content_item_id)?.title,
        content_prompt: contentById.get(item.content_item_id)?.prompt,
      }));

      setFeedback(formattedFeedback);

      // 4) Calculate stats
      const likes = formattedFeedback.filter(f => f.feedback_type === 'like').length;
      const dislikes = formattedFeedback.filter(f => f.feedback_type === 'dislike').length;
      setStats({ likes, dislikes, total: likes + dislikes });

    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 w-20 bg-muted animate-pulse rounded"></div>
                <div className="h-8 w-8 bg-muted animate-pulse rounded"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-muted animate-pulse rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <div className="h-6 w-32 bg-muted animate-pulse rounded"></div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-muted animate-pulse rounded"></div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Feedback</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Positive</CardTitle>
            <ThumbsUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.likes}</div>
            <p className="text-xs text-muted-foreground">
              {stats.total > 0 ? Math.round((stats.likes / stats.total) * 100) : 0}% of total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Negative</CardTitle>
            <ThumbsDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.dislikes}</div>
            <p className="text-xs text-muted-foreground">
              {stats.total > 0 ? Math.round((stats.dislikes / stats.total) * 100) : 0}% of total
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Feedback</CardTitle>
        </CardHeader>
        <CardContent>
          {feedback.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No feedback received yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Content</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feedback.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Badge 
                        variant={item.feedback_type === 'like' ? 'default' : 'destructive'}
                        className="flex items-center gap-1 w-fit"
                      >
                        {item.feedback_type === 'like' ? (
                          <ThumbsUp className="w-3 h-3" />
                        ) : (
                          <ThumbsDown className="w-3 h-3" />
                        )}
                        {item.feedback_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{item.user_email || 'Unknown'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs">
                        <div className="text-sm font-medium truncate">
                          {item.content_title || 'Untitled'}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {item.content_prompt || 'No prompt'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs">
                        {item.reason ? (
                          <span className="text-sm">{item.reason}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground italic">No reason provided</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{formatDate(item.created_at)}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};